import { upsSendJudas } from 'actionCreators/judasLog';
import defaultAvatar from 'assets/img/avatar.png';
import { useState, useCallback, SyntheticEvent, useEffect } from 'react';
import { hjTagging, openUpsell } from 'utils';
import { makeStyles } from '@material-ui/core/styles';
import upsellService from 'services/upsell';
import {
  Typography,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Button,
  alpha,
  useMediaQuery,
} from '@material-ui/core';
import ImgBannerLarge from 'assets/img/showcase-banner-large.png';
import ImgBannerMedium from 'assets/img/showcase-banner-medium.png';
import ImgBannerSmall from 'assets/img/showcase-banner-small.png';
import { ChevronRight } from '@eduzz/houston-icons';
import { IShowcaseBannerUpsell } from 'interface/upsell';
import useImageFallback from 'hooks/useImageFallback';
import { useAppSelector } from 'hooks/redux';
import { isSubdomain } from 'utils/domain';

const useStyles = makeStyles((theme) => ({
  banner: {
    position: 'relative',
    maxWidth: '100%',
    minHeight: 300,
    overflow: 'hidden',
    cursor: 'pointer',
    '&:hover': {
      '& .houston-icon': {
        opacity: 0,
      },
      '& #upsell-showcase-banner-info': {
        transform: 'translateX(0px)',
      },
    },
    '& img[data-show-image=false]': {
      display: 'none',
    },
    '& img[data-show-image=true]': {
      display: 'block',
    },
  },
  image: {
    width: '100%',
  },
  contentContainer: {
    '@media (max-width: 1023px)': {
      position: 'relative',
      width: 'auto',
      background: 'none',
    },
    '@media (min-width: 1024px)': {
      top: 0,
      bottom: 0,
      position: 'absolute',
      left: 0,
      width: 600,
      minWidth: 600,
      maxWidth: 600,
      transform: 'translateX(-600px)',
      transition: '0.4s',
      '& > div': {
        maxHeight: '100%',
      },
      '&:after': {
        content: "''",
        right: -100,
        position: 'absolute',
        background: alpha(theme.palette.neutrals.black, 0.7),
        height: '100%',
        clipPath: 'polygon(0 0, 0% 0%, 100% 0%, 0 100%)',
        width: 100,
      },
    },
    '& .houston-icon': {
      transition: '0.4s',
      transform: 'translateX(38px)',
      color: theme.palette.common.white,
      zIndex: 99,
    },
    background: alpha(theme.palette.neutrals.black, 0.7),
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    '& p': {
      fontFamily: 'Open Sans, sans-serif',
      color: '#FFFFFF',
    },
    '& > p': {
      marginBottom: 8,
      '&:nth-child(2)': {
        flexGrow: 0.9,
        overflow: 'hidden',
      },
    },
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
    padding: 16,
    boxSizing: 'border-box',
  },
  title: {
    color: theme.palette.common.white,
    maxWidth: '100%',
    wordBreak: 'break-word',
    fontWeight: 700,
    '@media (min-width: 1024px)': {
      fontSize: 20,
    },
    '@media (min-width: 1440px)': {
      fontSize: 32,
    },
  },
  author: {
    fontFamily: 'Open Sans, sans-serif',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    margin: 0,
  },
  containerAuthor: {
    padding: theme.spacing(4, 0),
    '@media (min-width: 1024px)': {
      display: 'none',
    },
    '@media (min-width: 1920px)': {
      display: 'block',
    },
  },
  button: {
    width: 'fit-content',
    '& > span': {
      fontSize: 14,
      textTransform: 'none',
      fontWeight: 600,
      width: 'fit-content',
    },
  },
  description: {
    wordWrap: 'break-word',
    '@media (min-width: 1024px)': {
      margin: theme.spacing(2, 0),
      fontSize: 14,
    },
    '@media (min-width: 1440px)': {
      margin: theme.spacing(2, 0),
      fontSize: 16,
    },
    '@media (min-width: 1920px)': {
      marginTop: theme.spacing(6),
    },
  },
}));

const UpsellBanner = () => {
  const [upsell, setUpsell] = useState<IShowcaseBannerUpsell>(null);
  const user = useAppSelector((store) => store.user);
  const avatarProps = useImageFallback(upsell?.producer?.avatar, defaultAvatar);
  const smallImageProps = useImageFallback(upsell?.highlight_images?.small, isSubdomain() ? null : ImgBannerSmall);
  const mediumImageProps = useImageFallback(upsell?.highlight_images?.medium, isSubdomain() ? null : ImgBannerMedium);
  const largeImageProps = useImageFallback(upsell?.highlight_images?.large, isSubdomain() ? null : ImgBannerLarge);
  const isScreenSmall = useMediaQuery('(max-width: 600px)');
  const isScreenMedium = useMediaQuery('(min-width: 601px) and (max-width: 960px)');
  const isScreenLarge = useMediaQuery('(min-width: 961px)');
  const classes = useStyles();

  const sendLog = useCallback(
    (action: 'click' | 'view') =>
      upsSendJudas({
        action,
        id: upsell.id,
        hash_checkout: upsell.hash,
        viewd: 'highlight',
        producer_id: upsell.producer.id,
        user_id: user.id,
      }),
    [upsell, user.id]
  );

  const sendLogsAndOpenUpsell = (event: SyntheticEvent) => {
    if (!upsell) return;
    event.stopPropagation();
    hjTagging('VITRINE-DESTAQUE-CLICOU');
    sendLog('click')();
    openUpsell(event, upsell, 'DESTAQUE', user);
  };

  useEffect(() => {
    if (!upsell) return;
    hjTagging('VITRINE-DESTAQUE');
    sendLog('view')();
  }, [sendLog, upsell]);

  useEffect(() => {
    upsellService
      .getShowcaseBannerUpsell()
      .then(([upsell]) => upsell)
      .then(setUpsell);
  }, []);

  const renderImages = (
    <>
      <img
        id='upsell-showcase-banner-image-small'
        data-show-image={isScreenSmall}
        className={classes.image}
        alt={'Imagem da oferta do produtor'}
        {...smallImageProps}
      />
      <img
        id='upsell-showcase-banner-image-medium'
        data-show-image={isScreenMedium}
        className={classes.image}
        alt={'Imagem da oferta do produtor'}
        {...mediumImageProps}
      />
      <img
        id='upsell-showcase-banner-image-large'
        data-show-image={isScreenLarge}
        className={classes.image}
        alt={'Imagem da oferta do produtor'}
        {...largeImageProps}
      />
    </>
  );

  if (isSubdomain() && !upsell) {
    return null;
  }

  if (!upsell) {
    return <div className={classes.banner}>{renderImages}</div>;
  }

  return (
    <div className={classes.banner} onClick={sendLogsAndOpenUpsell}>
      {renderImages}
      <div id='upsell-showcase-banner-info' className={classes.contentContainer}>
        <div className={classes.content}>
          <Typography variant='h2' className={classes.title}>
            {upsell.title.toUpperCase()}
          </Typography>

          <Typography className={classes.description}>{upsell.description}</Typography>

          <div className={classes.containerAuthor}>
            <ListItem className={classes.containerAuthor}>
              <ListItemAvatar>
                <Avatar alt='Avatar do produtor da oferta' {...avatarProps} />
              </ListItemAvatar>
              <ListItemText
                classes={{ secondary: classes.author }}
                secondary={upsell.producer && upsell.producer.name}
              />
            </ListItem>
          </div>

          <Button
            id='upsell-showcase-banner-button'
            className={classes.button}
            color='primary'
            variant='contained'
            onClick={sendLogsAndOpenUpsell}
          >
            {upsell.label_text || 'Saiba Mais'}
          </Button>
        </div>

        <ChevronRight id='upsell-showcase-banner-chevron-right' size={32} />
      </div>
    </div>
  );
};

export default UpsellBanner;
